import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, CheckCircle, Award, Factory, Globe, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Home = () => {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);

  const heroImages = [
    'https://customer-assets.emergentagent.com/job_dharani-pipes/artifacts/toejrxle_Gemini_Generated_Image_tn94pctn94pctn94.png',
    'https://customer-assets.emergentagent.com/job_dharani-pipes/artifacts/gviwsb6a_Gemini_Generated_Image_156kmz156kmz156k.png',
    'https://customer-assets.emergentagent.com/job_dharani-pipes/artifacts/bgnwdm49_Gemini_Generated_Image_jtb52jtb52jtb52j.png'
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(timer);
  }, [heroImages.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroImages.length) % heroImages.length);
  };

  const [currentProductSlide, setCurrentProductSlide] = useState(0);

  const products = [
    { 
      name: t('products.upvcAgriculture.name'),
      range: t('products.upvcAgriculture.range'),
      standard: t('products.upvcAgriculture.standard'),
      image: 'https://customer-assets.emergentagent.com/job_dharani-pipes/artifacts/gb9eiulb_Gemini_Generated_Image_9lu28f9lu28f9lu2.png',
      description: t('products.upvcAgriculture.description')
    },
    { 
      name: t('products.swr.name'),
      range: t('products.swr.range'),
      standard: t('products.swr.standard'),
      image: 'https://customer-assets.emergentagent.com/job_dharani-pipes/artifacts/742f2j7z_WhatsApp%20Image%202026-03-14%20at%207.58.27%20PM.jpeg',
      description: t('products.swr.description')
    },
    { 
      name: t('products.conduits.name'),
      range: t('products.conduits.range'),
      standard: t('products.conduits.standard'),
      image: 'https://customer-assets.emergentagent.com/job_dharani-pipes/artifacts/l6czzc4r_Gemini_Generated_Image_j99ajbj99ajbj99a.png',
      description: t('products.conduits.description')
    },
    { 
      name: t('products.ugd.name'),
      range: t('products.ugd.range'),
      standard: t('products.ugd.standard'),
      image: 'https://customer-assets.emergentagent.com/job_dharani-pipes/artifacts/q5hdaleh_Gemini_Generated_Image_t19nayt19nayt19n.png',
      description: t('products.ugd.description')
    },
    { 
      name: t('products.plumbing.name'),
      range: t('products.plumbing.range'),
      standard: t('products.plumbing.standard'),
      image: 'https://customer-assets.emergentagent.com/job_dharani-pipes/artifacts/pn57daeh_Gemini_Generated_Image_b8u3axb8u3axb8u3.png',
      description: t('products.plumbing.description')
    },
    { 
      name: t('products.casing.name'),
      range: t('products.casing.range'),
      standard: t('products.casing.standard'),
      image: 'https://customer-assets.emergentagent.com/job_dharani-pipes/artifacts/pula3ot3_ChatGPT%20Image%20Mar%2014%2C%202026%2C%2005_49_28%20PM.png',
      description: t('products.casing.description')
    },
  ];

  const nextProductSlide = () => {
    setCurrentProductSlide((prev) => (prev + 1) % products.length);
  };

  const prevProductSlide = () => {
    setCurrentProductSlide((prev) => (prev - 1 + products.length) % products.length);
  };

  const features = [
    { icon: Award, title: t('home.features.iso.title'), description: t('home.features.iso.description') },
    { icon: Factory, title: t('home.features.experience.title'), description: t('home.features.experience.description') },
    { icon: Globe, title: t('home.features.range.title'), description: t('home.features.range.description') },
    { icon: CheckCircle, title: t('home.features.solar.title'), description: t('home.features.solar.description') },
    { icon: CheckCircle, title: t('home.features.quality.title'), description: t('home.features.quality.description') },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section with Image Slider - Full width on mobile */}
      <section 
        className="relative h-[500px] md:h-[600px] overflow-hidden group"
      >
        {/* Image Slider - Full visibility, edge-to-edge on mobile */}
        <div className="absolute inset-0">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img 
                src={image} 
                alt={`Dharani Pipes ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
        </div>

        {/* Lighter gradient overlay - only where text is */}
        <div className="absolute inset-y-0 left-0 right-1/2 md:right-2/3 bg-gradient-to-r from-black/50 via-black/30 to-transparent"></div>

        {/* Navigation Arrows - Hidden by default, visible on hover */}
        <button
          onClick={prevSlide}
          className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/30 text-white p-2 md:p-3 rounded-full backdrop-blur-sm transition-all duration-300 hover:scale-110 opacity-0 group-hover:opacity-100"
          aria-label="Previous slide"
        >
          <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/30 text-white p-2 md:p-3 rounded-full backdrop-blur-sm transition-all duration-300 hover:scale-110 opacity-0 group-hover:opacity-100"
          aria-label="Next slide"
        >
          <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
        </button>

        {/* Slide Indicators */}
        <div className="absolute bottom-4 md:bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentSlide ? 'w-8 bg-white' : 'w-2 bg-white/50'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
        
        {/* Text Content - Enhanced text shadow for readability */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-start pt-20 md:pt-32 z-10">
          <div className="text-white max-w-2xl">
            <Badge className="mb-3 md:mb-4 bg-white text-[#4A5F8F] hover:bg-gray-100 text-xs md:text-sm shadow-lg">
              ISO 9001:2015 Certified
            </Badge>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 leading-tight" 
                style={{ textShadow: '2px 2px 8px rgba(0,0,0,0.8), 0 0 15px rgba(0,0,0,0.5)' }}>
              {t('home.hero.title')}
            </h1>
            <p className="text-xl mb-8 text-gray-100">
              {t('home.hero.subtitle')}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/products">
                <Button size="lg" className="bg-white text-[#4A5F8F] hover:bg-gray-100">
                  {t('home.hero.cta')} <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#4A5F8F]">
                  {t('home.hero.ctaSecondary')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - Clean Design with Mobile Stacking */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('home.whyChoose.title')}</h2>
            <p className="text-xl text-gray-600">{t('home.whyChoose.subtitle')}</p>
          </div>
          
          {/* 2 columns on mobile, 3 on tablet, 5 on desktop */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden flex flex-col h-full">
                <div className="p-4 md:p-6 text-center flex-grow flex flex-col">
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-[#4A5F8F] bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                    <feature.icon className="w-7 h-7 md:w-8 md:h-8 text-[#4A5F8F]" />
                  </div>
                  <h3 className="text-sm md:text-lg font-semibold text-gray-900 mb-2 leading-tight">
                    {feature.title}
                  </h3>
                  <p className="text-xs md:text-base text-gray-600 leading-relaxed flex-grow">{feature.description}</p>
                </div>
                {/* Blue accent line at bottom - always at the same position */}
                <div className="h-1 bg-[#4A5F8F] mt-auto" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Preview Section - Enhanced with Images & Carousel */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('home.products.title')}</h2>
            <p className="text-xl text-gray-600">{t('home.products.subtitle')}</p>
          </div>
          
          {/* Mobile: Carousel (1 product at a time) */}
          <div className="md:hidden relative">
            <div className="overflow-hidden">
              <div 
                className="flex items-stretch transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentProductSlide * 100}%)` }}
              >
                {products.map((product, index) => (
                  <div key={index} className="w-full flex-shrink-0 px-4 flex">
                    <div className="bg-white rounded-xl shadow-xl overflow-hidden border-2 border-gray-100 hover:shadow-2xl transition-all duration-300 w-full flex flex-col">
                      {/* Product Image */}
                      <div className="relative h-48 overflow-hidden bg-gray-100 flex-shrink-0">
                        <img 
                          src={product.image} 
                          alt={product.name}
                          className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute top-3 right-3">
                          <span className="text-xs font-bold px-3 py-1 rounded-full bg-[#4A5F8F] text-white shadow-lg">
                            {product.standard}
                          </span>
                        </div>
                      </div>
                      
                      {/* Product Content */}
                      <div className="p-6 flex-grow flex flex-col">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                          {product.name}
                        </h3>
                        <p className="text-sm text-gray-600 mb-3 flex-grow">{product.description}</p>
                        <p className="text-base text-gray-700 font-medium mb-4">
                          Size Range: {product.range}
                        </p>
                        <div className="flex items-center text-[#4A5F8F] font-medium">
                          <CheckCircle className="w-5 h-5 mr-2" />
                          {t('home.features.quality.title')}
                        </div>
                      </div>
                      
                      {/* Blue accent line */}
                      <div className="h-1 bg-[#4A5F8F] flex-shrink-0" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Carousel Navigation */}
            <button
              onClick={prevProductSlide}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white text-[#4A5F8F] p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
              aria-label="Previous product"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={nextProductSlide}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white text-[#4A5F8F] p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
              aria-label="Next product"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
            
            {/* Carousel Indicators */}
            <div className="flex justify-center gap-2 mt-6">
              {products.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentProductSlide(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === currentProductSlide ? 'w-8 bg-[#4A5F8F]' : 'w-2 bg-gray-300'
                  }`}
                  aria-label={`Go to product ${index + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Desktop: Carousel showing 3 products at once */}
          <div className="hidden md:block relative">
            <div className="overflow-hidden">
              <div 
                className="flex items-stretch transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentProductSlide * 33.333}%)` }}
              >
                {products.map((product, index) => (
                  <div key={index} className="w-1/3 flex-shrink-0 px-4 flex">
                    <div className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-gray-100 overflow-hidden group w-full flex flex-col">
                      {/* Product Image */}
                      <div className="relative h-56 overflow-hidden bg-gray-100 flex-shrink-0">
                        <img 
                          src={product.image} 
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute top-4 right-4">
                          <span className="text-sm font-bold px-4 py-2 rounded-full bg-[#4A5F8F] text-white shadow-lg">
                            {product.standard}
                          </span>
                        </div>
                      </div>
                      
                      {/* Product Content */}
                      <div className="p-6 flex-grow flex flex-col">
                        <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-[#4A5F8F] transition-colors">
                          {product.name}
                        </h3>
                        <p className="text-sm text-gray-600 mb-3 leading-relaxed flex-grow">{product.description}</p>
                        <p className="text-base text-gray-700 font-medium mb-4">
                          Size Range: {product.range}
                        </p>
                        <div className="flex items-center text-[#4A5F8F] font-medium">
                          <CheckCircle className="w-5 h-5 mr-2" />
                          {t('home.features.quality.title')}
                        </div>
                      </div>
                      
                      {/* Blue accent line */}
                      <div className="h-1 bg-[#4A5F8F] flex-shrink-0" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Desktop Carousel Navigation */}
            <button
              onClick={prevProductSlide}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white text-[#4A5F8F] p-4 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
              aria-label="Previous products"
            >
              <ChevronLeft className="w-7 h-7" />
            </button>
            <button
              onClick={nextProductSlide}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white text-[#4A5F8F] p-4 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
              aria-label="Next products"
            >
              <ChevronRight className="w-7 h-7" />
            </button>
            
            {/* Desktop Carousel Indicators */}
            <div className="flex justify-center gap-2 mt-8">
              {products.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentProductSlide(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === currentProductSlide ? 'w-8 bg-[#4A5F8F]' : 'w-2 bg-gray-300'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
          
          <div className="text-center mt-12">
            <Link to="/products">
              <Button size="lg" className="bg-[#4A5F8F] hover:bg-[#3A4F7F]">
                View All Products <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">{t('about.title')}</h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed" dangerouslySetInnerHTML={{ __html: t('home.aboutPreview.para1') }} />
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                {t('home.aboutPreview.para2')}
              </p>
              <Link to="/about">
                <Button size="lg" className="bg-[#4A5F8F] hover:bg-[#3A4F7F]">
                  {t('common.learnMore')} <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
            <div className="relative h-[400px] rounded-lg overflow-hidden shadow-2xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_dharani-pipes/artifacts/8stk6h1u_Gemini_Generated_Image_156kmz156kmz156k.png" 
                alt="Dharani Pipes Manufacturing Facility" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#4A5F8F] to-[#2C3E5F]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">{t('home.cta.title')}</h2>
          <p className="text-xl text-gray-100 mb-8">
            {t('home.cta.subtitle')}
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-[#4A5F8F] hover:bg-gray-100">
              {t('home.cta.button')} <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
